import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Calendar, Clock, Eye, ArrowLeft, ArrowRight, ChevronRight, Facebook, Twitter, MessageCircle, Link as LinkIcon } from "lucide-react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import JsonLd from "@/components/seo/JsonLd";
import api from "@/lib/api";

const FALLBACK_COVER = "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&q=80&auto=format&fit=crop";

/** ~200 words / minute is the industry-standard reading pace. Falls back to
 *  a minimum of 1 min so short posts don't awkwardly show "0 min read". */
function readingTime(text = "") {
  const words = String(text).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

/** Card used in the blog index + related-posts strip. */
function PostCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group flex flex-col rounded-2xl bg-white border border-[#E7DDCF] overflow-hidden hover:shadow-[0_10px_28px_rgba(17,24,39,0.08)] transition-shadow"
    >
      <div className="aspect-[16/9] bg-[#F5EFE6] overflow-hidden">
        <img
          src={post.cover_image || FALLBACK_COVER}
          alt={post.title}
          loading="lazy"
          onError={(e) => { if (e.currentTarget.src !== FALLBACK_COVER) e.currentTarget.src = FALLBACK_COVER; }}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="text-[11px] uppercase tracking-widest text-[#7A5A22]">{post.category}</div>
        <h3 className="font-heading text-lg text-[#111827] mt-1 leading-snug">{post.title}</h3>
        {post.excerpt && (
          <p className="text-sm text-[#4B5563] mt-2 line-clamp-3">{post.excerpt}</p>
        )}
        <div className="mt-4 pt-3 border-t border-[#E7DDCF]/60 flex items-center gap-3 text-[11px] text-[#4B5563]">
          <span className="flex items-center gap-1"><Calendar size={12} /> {formatDate(post.published_at)}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {readingTime(post.excerpt || post.title)}+ min read</span>
          {typeof post.views === "number" && (
            <span className="flex items-center gap-1"><Eye size={12} /> {post.views}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

/** ---------------------------------------------------------------------
 *  Blog Index — `/blog` and `/blog/category/:category`
 *  --------------------------------------------------------------------- */
export function BlogIndex() {
  const { category: routeCategory } = useParams();
  const [sp, setSp] = useSearchParams();
  const activeCategory = routeCategory ? decodeURIComponent(routeCategory) : null;
  const q = sp.get("q") || "";
  const page = Number(sp.get("page") || 1);

  const [posts, setPosts] = useState(null);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const pageSize = 12;

  useEffect(() => {
    document.title = activeCategory
      ? `${activeCategory} - Blog - Pune Spa Guide`
      : "Wellness Blog - Pune Spa Guide";
  }, [activeCategory]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/blog/categories");
        setCategories(r.data.items);
      } catch (err) {
        // Non-blocking — categories power a filter chip strip; homepage still works without it.
        // Kept quiet in the UI but logged so we can spot backend outages.
        if (process.env.NODE_ENV !== "production") console.warn("Blog categories fetch failed:", err);
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPosts(null);
    (async () => {
      try {
        const params = { page, page_size: pageSize };
        if (activeCategory) params.category = activeCategory;
        if (q) params.q = q;
        const r = await api.get("/blog/posts", { params });
        if (cancelled) return;
        setPosts(r.data.items);
        setTotal(r.data.total);
      } catch {
        if (!cancelled) { setPosts([]); setTotal(0); }
      }
    })();
    return () => { cancelled = true; };
  }, [activeCategory, q, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const setPage = (n) => setSp((prev) => { const p = new URLSearchParams(prev); p.set("page", String(n)); return p; });

  return (
    <div data-testid="blog-index-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Insights & Guides</div>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#111827] mt-1">
          {activeCategory || "Wellness Blog"}
        </h1>
        <p className="mt-2 text-[#4B5563] max-w-2xl">
          Spa treatments, wellness tips, ayurvedic wisdom and curated guides for Pune & Pimpri-Chinchwad.
        </p>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-category-chips">
        <Link
          to="/blog"
          data-testid="chip-all"
          className={`px-3 py-1 rounded-full text-xs border ${!activeCategory ? "bg-[#1F4E3D] text-white border-[#1F4E3D]" : "bg-white text-[#4B5563] border-[#E7DDCF] hover:border-[#C9A86A]"}`}
        >
          All ({total || categories.reduce((sum, c) => sum + c.count, 0)})
        </Link>
        {categories.map((c) => {
          const on = activeCategory === c.name;
          return (
            <Link
              key={c.name}
              to={`/blog/category/${encodeURIComponent(c.name)}`}
              data-testid={`chip-${c.name}`}
              className={`px-3 py-1 rounded-full text-xs border ${on ? "bg-[#1F4E3D] text-white border-[#1F4E3D]" : "bg-white text-[#4B5563] border-[#E7DDCF] hover:border-[#C9A86A]"}`}
            >
              {c.name} ({c.count})
            </Link>
          );
        })}
      </div>

      {posts === null ? (
        <div data-testid="blog-loading" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {["s1", "s2", "s3", "s4", "s5", "s6"].map((k) => (
            <div key={k} className="rounded-2xl bg-white border border-[#E7DDCF] overflow-hidden animate-pulse">
              <div className="aspect-[16/9] bg-[#F5EFE6]" />
              <div className="p-5 space-y-3">
                <div className="h-3 w-24 bg-[#F5EFE6] rounded" />
                <div className="h-5 w-4/5 bg-[#F5EFE6] rounded" />
                <div className="h-4 w-full bg-[#F5EFE6] rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div data-testid="blog-empty" className="rounded-2xl bg-white border border-dashed border-[#E7DDCF] p-10 text-center text-[#4B5563]">
          {activeCategory
            ? `No posts published under "${activeCategory}" yet. Check back soon!`
            : "No blog posts published yet. Check back soon!"}
        </div>
      ) : (
        <div data-testid="blog-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => <PostCard key={p.id} post={p} />)}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-3" data-testid="blog-pagination">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="blog-prev-page">
            <ArrowLeft size={14} className="mr-1" /> Previous
          </Button>
          <span className="text-sm text-[#4B5563]">Page {page} of {totalPages}</span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="blog-next-page">
            Next <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

/** ---------------------------------------------------------------------
 *  Blog Post Detail — `/blog/:slug`
 *  --------------------------------------------------------------------- */
export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPost(null); setNotFound(false); setRelated([]);
    (async () => {
      try {
        const r = await api.get(`/blog/posts/${slug}`);
        if (cancelled) return;
        setPost(r.data.post);
        setRelated(r.data.related || []);
        document.title = `${r.data.post.seo_title || r.data.post.title} - Pune Spa Guide`;
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Render sanitised HTML from the markdown body. We sanitise AFTER the
  // markdown pass so `marked`'s output can't smuggle in <script> tags.
  const contentHtml = useMemo(() => {
    if (!post?.content_md) return "";
    const raw = marked.parse(post.content_md, { async: false, breaks: true, gfm: true });
    return DOMPurify.sanitize(raw);
  }, [post?.content_md]);

  const wordsSource = post?.content_md || "";
  const readMin = readingTime(wordsSource);

  const share = useCallback((platform) => {
    if (!post) return;
    const url = typeof window !== "undefined" ? window.location.href : "";
    const text = encodeURIComponent(post.title);
    const enc = encodeURIComponent(url);
    if (platform === "copy") {
      navigator.clipboard?.writeText(url).then(
        () => toast.success("Link copied to clipboard!"),
        () => toast.error("Could not copy link — please copy the URL manually."),
      );
      return;
    }
    const targets = {
      whatsapp: `https://wa.me/?text=${text}%20${enc}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${enc}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc}`,
    };
    if (targets[platform]) window.open(targets[platform], "_blank", "noopener,noreferrer");
  }, [post]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading text-3xl text-[#111827]">Post not found</h1>
        <p className="mt-2 text-[#4B5563]">The article you&apos;re looking for doesn&apos;t exist or hasn&apos;t been published yet.</p>
        <Link to="/blog" className="inline-flex items-center gap-1 mt-6 text-[#1F4E3D] hover:underline">
          <ArrowLeft size={14} /> Back to blog
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-14 animate-pulse space-y-4" data-testid="blog-post-loading">
        <div className="h-4 w-32 bg-[#F5EFE6] rounded" />
        <div className="h-10 w-4/5 bg-[#F5EFE6] rounded" />
        <div className="aspect-[16/9] bg-[#F5EFE6] rounded-2xl" />
        <div className="space-y-2 pt-4">
          <div className="h-4 w-full bg-[#F5EFE6] rounded" />
          <div className="h-4 w-11/12 bg-[#F5EFE6] rounded" />
          <div className="h-4 w-10/12 bg-[#F5EFE6] rounded" />
        </div>
      </div>
    );
  }

  const canonicalUrl = typeof window !== "undefined" ? window.location.href : "";
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.seo_description || post.excerpt,
    image: post.cover_image || FALLBACK_COVER,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: post.author || "Pune Spa Guide Editorial" },
    publisher: {
      "@type": "Organization",
      name: "Pune Spa Guide",
      logo: { "@type": "ImageObject", url: `${window.location.origin}/logo-header.png` },
    },
    mainEntityOfPage: canonicalUrl,
  };

  return (
    <article data-testid="blog-post-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <JsonLd data={articleSchema} />

      {/* Breadcrumb */}
      <nav className="text-xs text-[#4B5563] flex items-center gap-1 mb-6" aria-label="Breadcrumb" data-testid="blog-breadcrumb">
        <Link to="/" className="hover:text-[#1F4E3D]">Home</Link>
        <ChevronRight size={12} />
        <Link to="/blog" className="hover:text-[#1F4E3D]">Blog</Link>
        <ChevronRight size={12} />
        <Link to={`/blog/category/${encodeURIComponent(post.category)}`} className="hover:text-[#1F4E3D]">
          {post.category}
        </Link>
      </nav>

      <div className="text-xs uppercase tracking-widest text-[#7A5A22]" data-testid="blog-post-category">{post.category}</div>
      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#111827] mt-2 leading-tight">
        {post.title}
      </h1>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#4B5563]">
        <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#1F4E3D]" /> {formatDate(post.published_at)}</span>
        <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#1F4E3D]" /> {readMin} min read</span>
        {typeof post.views === "number" && (
          <span className="flex items-center gap-1.5" data-testid="blog-post-views"><Eye size={14} className="text-[#1F4E3D]" /> {post.views} views</span>
        )}
        <span className="flex items-center gap-1.5">By <strong className="text-[#111827]">{post.author}</strong></span>
      </div>

      {post.cover_image && (
        <div className="mt-6 rounded-2xl overflow-hidden bg-[#F5EFE6] aspect-[16/9]">
          <img
            src={post.cover_image}
            alt={post.title}
            onError={(e) => { if (e.currentTarget.src !== FALLBACK_COVER) e.currentTarget.src = FALLBACK_COVER; }}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {post.excerpt && (
        <p className="mt-6 text-lg text-[#4B5563] leading-relaxed italic border-l-4 border-[#C9A86A] pl-4">
          {post.excerpt}
        </p>
      )}

      {/* Markdown body */}
      <div
        className="prose-content mt-8 text-[#1F2937] leading-relaxed"
        data-testid="blog-post-body"
        dangerouslySetInnerHTML={{ __html: contentHtml }}
      />

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2" data-testid="blog-post-tags">
          {post.tags.map((t) => (
            <span key={t} className="px-2.5 py-1 rounded-full text-[11px] bg-[#F5EFE6] text-[#7A5A22] border border-[#E7DDCF]">
              #{t}
            </span>
          ))}
        </div>
      )}

      {/* Share bar */}
      <div className="mt-10 pt-6 border-t border-[#E7DDCF] flex items-center gap-3" data-testid="blog-share-bar">
        <span className="text-xs uppercase tracking-widest text-[#7A5A22] mr-2">Share</span>
        <button onClick={() => share("whatsapp")} aria-label="Share on WhatsApp" data-testid="share-whatsapp" className="h-9 w-9 rounded-full border border-[#E7DDCF] bg-white flex items-center justify-center text-[#25D366] hover:border-[#25D366]">
          <MessageCircle size={16} />
        </button>
        <button onClick={() => share("twitter")} aria-label="Share on Twitter" data-testid="share-twitter" className="h-9 w-9 rounded-full border border-[#E7DDCF] bg-white flex items-center justify-center text-[#1DA1F2] hover:border-[#1DA1F2]">
          <Twitter size={16} />
        </button>
        <button onClick={() => share("facebook")} aria-label="Share on Facebook" data-testid="share-facebook" className="h-9 w-9 rounded-full border border-[#E7DDCF] bg-white flex items-center justify-center text-[#1877F2] hover:border-[#1877F2]">
          <Facebook size={16} />
        </button>
        <button onClick={() => share("copy")} aria-label="Copy link" data-testid="share-copy" className="h-9 w-9 rounded-full border border-[#E7DDCF] bg-white flex items-center justify-center text-[#1F4E3D] hover:border-[#1F4E3D]">
          <LinkIcon size={16} />
        </button>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-14" data-testid="blog-related">
          <h2 className="font-heading text-2xl text-[#111827] mb-5">Related articles</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {related.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        </section>
      )}
    </article>
  );
}

export default BlogIndex;
